import React from 'react';
import { Paper, Typography, Box, CircularProgress } from '@mui/material';
import Chart from 'react-apexcharts';
import { formatCurrency } from '../../../utils/formatCurrency';
import { DASHBOARD_COLORS, sectionPaperSx } from '../dashboardStyles';
import { getSalesOverviewKpiConfig, buildBaseChartOptions } from './salesOverviewConstants';
import SalesOverviewHeader from './SalesOverviewHeader';

/** Template 1 — original area chart with KPI row on top. */
const SalesOverviewTemplate1 = ({
  labels,
  data,
  metrics,
  loading,
  compact = false,
  period = 'monthly',
  onPeriodChange,
  showAiAssistant = false,
  onAiAssistantClick,
}) => {
  const kpiConfig = getSalesOverviewKpiConfig(period);
  const chartHeight = compact ? 120 : 240;
  const chartOptions = {
    ...buildBaseChartOptions(labels),
    colors: ['#2563eb'],
    stroke: { curve: 'smooth', width: 2.5, lineCap: 'round' },
    markers: {
      size: compact ? 2 : 4,
      colors: ['#2563eb'],
      strokeColors: '#fff',
      strokeWidth: 2,
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 0.2,
        opacityFrom: 0.35,
        opacityTo: 0.04,
        stops: [0, 100],
      },
    },
    tooltip: { y: { formatter: (v) => formatCurrency(v) } },
  };

  const series = [{ name: 'Sales', data: data || [] }];

  return (
    <Paper
      elevation={0}
      sx={{
        ...sectionPaperSx,
        height: '100%',
        minHeight: compact ? 0 : 320,
        display: 'flex',
        flexDirection: 'column',
        p: compact ? 1.5 : 2.5,
      }}
    >
      <SalesOverviewHeader
        period={period}
        onPeriodChange={onPeriodChange}
        compact={compact}
        showPeriodToggle={!compact && Boolean(onPeriodChange)}
        showAiAssistant={showAiAssistant}
        onAiAssistantClick={onAiAssistantClick}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 0,
          mb: compact ? 1 : 2,
          border: `1px solid ${DASHBOARD_COLORS.border}`,
          borderRadius: 1.5,
          overflow: 'hidden',
        }}
      >
        {kpiConfig.map((kpi, index) => (
          <Box
            key={kpi.key}
            sx={{
              p: compact ? 0.75 : 1.5,
              textAlign: 'center',
              borderRight: index < kpiConfig.length - 1 ? `1px solid ${DASHBOARD_COLORS.border}` : 'none',
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: compact ? '0.7rem' : { xs: '0.95rem', sm: '1.05rem' },
                color: kpi.color,
                lineHeight: 1.2,
              }}
            >
              {loading ? '—' : formatCurrency(metrics[kpi.key] ?? 0)}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: DASHBOARD_COLORS.textMuted, fontWeight: 500, fontSize: compact ? '0.6rem' : undefined }}
            >
              {kpi.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ flex: 1, minHeight: compact ? 100 : 200 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress size={compact ? 24 : 32} />
          </Box>
        ) : (
          <Chart options={chartOptions} series={series} type="area" height={chartHeight} />
        )}
      </Box>
    </Paper>
  );
};

export default SalesOverviewTemplate1;
